import { useEffect, useState, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadFailure } from '../../components/LoadFailure';
import type { Thresholds } from '../../core/thresholds';
import {
  CardHeader,
  CardHeading,
  CardSubline,
  ConfirmedLine,
  Footnote,
  Loading,
  Mark,
  NotConfirmedPill,
  Note,
  RowsFrame,
  SaveFailed,
  SettingsCard,
} from './cardParts';
import { confirmedLine } from './confirmation';
import { YearsChangedError, type Confirmation, type YearsSettings } from './settingsApi';
import { SETTINGS } from './tokens';
import {
  LONG_TERM_CHOICES,
  NEWCOMER_CHOICES,
  YEARS_DEFAULTS,
  longTermSentence,
  thresholdsFromForm,
  yearsDirty,
  yearsFormFrom,
  yearsLabel,
  type YearsForm,
} from './yearsModel';

export const YEARS_CONFIRMED_TOAST = 'Years confirmed. Counts and plans use them now.';
export const YEARS_SAVED_TOAST = 'Years saved. Counts and plans use them now.';
export const YEARS_DISCARD_BODY = 'Your changes to the newcomer and long-term years won’t be saved. Your district’s current years stay in place.';
export const CACHE_MISSED_MESSAGE = 'Saved for your district, but this browser couldn’t keep a copy. Refresh the page before opening Students or a plan.';
export const YEARS_CHANGED_MESSAGE = 'These years changed since you opened this page. Check them and confirm again.';

const NEWCOMER_TEXT = 'Counts as a newcomer for their first';
const LONG_TERM_TEXT = 'Counts as long-term once they’ve been an EL for more than';
const COUNTED_FROM = 'Counted from the school year each student was first classified as an EL.';

type Props = {
  years: () => Promise<YearsSettings>;
  confirm: (reviewed: Thresholds) => Promise<YearsSettings>;
  save: (values: Thresholds) => Promise<YearsSettings>;
  cacheThresholds: (values: Thresholds) => boolean;
  onToast: (message: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onSavingChange: (saving: boolean) => void;
  requestLeave: (proceed: () => void) => void;
};

function Row({ label, text, hint, children }: { label: string; text: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ p: '12px 16px', '& + &': { borderTop: `1px solid ${SETTINGS.divider}` } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.ink }}>
        <Typography component="span" sx={{ width: SETTINGS.labelWidth, flexShrink: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 700 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography component="span" sx={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
            {text}
          </Typography>
          {hint}
        </Box>
        {children}
      </Box>
    </Box>
  );
}

function ValueRow({ label, text, value, mark }: { label: string; text: string; value: string; mark?: string }) {
  return (
    <Box sx={{ p: '12px 16px', '& + &': { borderTop: `1px solid ${SETTINGS.divider}` } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '16px', fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.ink }}>
        <Typography component="span" sx={{ width: SETTINGS.labelWidth, flexShrink: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 700 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography component="span" sx={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
            {text}
          </Typography>
          {mark && <Mark>{mark}</Mark>}
        </Box>
        <Typography component="span" sx={{ fontFamily: 'inherit', fontSize: 18, lineHeight: '28px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function YearsSelect({ label, value, choices, changed, onChange }: { label: string; value: number; choices: number[]; changed: boolean; onChange: (n: number) => void }) {
  return (
    <Box
      component="select"
      aria-label={label}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(Number(e.target.value))}
      sx={{
        width: 136,
        height: 40,
        flexShrink: 0,
        px: '12px',
        borderRadius: '8px',
        border: changed ? `2px solid ${SETTINGS.primary}` : `1px solid ${SETTINGS.divider}`,
        bgcolor: SETTINGS.cardBg,
        color: SETTINGS.ink,
        fontFamily: SETTINGS.font,
        fontSize: 16,
        cursor: 'pointer',
      }}
    >
      {choices.map((n) => (
        <option key={n} value={n}>
          {yearsLabel(n)}
        </option>
      ))}
    </Box>
  );
}

function hint(value: number, fallback: number, choices: number[]): ReactNode {
  const range = `Choose ${choices[0]} to ${choices[choices.length - 1]} years.`;
  if (value === fallback) {
    return <Typography component="span" sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.muted }}>{range}</Typography>;
  }
  return <Mark>{`Changed from the default of ${fallback}. ${range}`}</Mark>;
}

export function YearsTab({ years, confirm, save, cacheThresholds, onToast, onDirtyChange, onSavingChange, requestLeave }: Props) {
  const [values, setValues] = useState<YearsSettings | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState<YearsForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [cacheMissed, setCacheMissed] = useState(false);
  const [changedSinceLoad, setChangedSinceLoad] = useState(false);

  useEffect(() => {
    let active = true;
    years()
      .then((nextValues) => {
        if (!active) return;
        setValues(nextValues);
        setConfirmation(nextValues.yearsConfirmed ?? null);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [years]);

  const saved = values ? yearsFormFrom(values) : null;
  const dirty = form !== null && saved !== null && yearsDirty(form, saved);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    onSavingChange(busy);
  }, [busy, onSavingChange]);

  useEffect(() => () => onSavingChange(false), [onSavingChange]);

  if (failed) return <LoadFailure tokens={SETTINGS} />;
  if (values === null || saved === null) return <Loading />;

  const onConfirm = () => {
    if (busy) return;
    setBusy(true);
    setSaveFailed(false);
    setChangedSinceLoad(false);
    confirm({ newcomerMaxYears: values.newcomerMaxYears, ltelMinYears: values.ltelMinYears, monitoringYears: values.monitoringYears })
      .then((confirmed) => {
        const cached = cacheThresholds({ newcomerMaxYears: confirmed.newcomerMaxYears, ltelMinYears: confirmed.ltelMinYears, monitoringYears: confirmed.monitoringYears });
        setCacheMissed(!cached);
        setValues(confirmed);
        setConfirmation(confirmed.yearsConfirmed ?? null);
        onToast(YEARS_CONFIRMED_TOAST);
      })
      .catch((err: unknown) => {
        if (err instanceof YearsChangedError) {
          const cached = cacheThresholds({ newcomerMaxYears: err.current.newcomerMaxYears, ltelMinYears: err.current.ltelMinYears, monitoringYears: err.current.monitoringYears });
          setCacheMissed(!cached);
          setValues(err.current);
          setConfirmation(err.current.yearsConfirmed ?? null);
          setChangedSinceLoad(true);
          return;
        }
        setSaveFailed(true);
      })
      .finally(() => setBusy(false));
  };

  const onSave = () => {
    if (busy || form === null) return;
    const thresholds = thresholdsFromForm(form, values.monitoringYears);
    setBusy(true);
    setSaveFailed(false);
    save(thresholds)
      .then((stored) => {
        const cached = cacheThresholds({ newcomerMaxYears: stored.newcomerMaxYears, ltelMinYears: stored.ltelMinYears, monitoringYears: stored.monitoringYears });
        setValues(stored);
        setConfirmation(stored.yearsConfirmed ?? null);
        setCacheMissed(!cached);
        setForm(null);
        onToast(YEARS_SAVED_TOAST);
      })
      .catch(() => setSaveFailed(true))
      .finally(() => setBusy(false));
  };

  if (form !== null) {
    const current = form;
    return (
      <SettingsCard labelledBy="years-title">
        <CardHeader>
          <CardHeading id="years-title">Edit newcomer and long-term years</CardHeading>
          <CardSubline>Starting from Flashlight’s defaults. Change only what’s different in your district.</CardSubline>
        </CardHeader>
        <RowsFrame>
          <Row label="Newcomer" text={NEWCOMER_TEXT} hint={hint(current.newcomer, YEARS_DEFAULTS.newcomer, NEWCOMER_CHOICES)}>
            <YearsSelect label="Newcomer years" value={current.newcomer} choices={NEWCOMER_CHOICES} changed={current.newcomer !== YEARS_DEFAULTS.newcomer} onChange={(n) => setForm({ ...current, newcomer: n })} />
          </Row>
          <Row label="Long-term ML" text={LONG_TERM_TEXT} hint={hint(current.longTerm, YEARS_DEFAULTS.longTerm, LONG_TERM_CHOICES)}>
            <YearsSelect label="Long-term years" value={current.longTerm} choices={LONG_TERM_CHOICES} changed={current.longTerm !== YEARS_DEFAULTS.longTerm} onChange={(n) => setForm({ ...current, longTerm: n })} />
          </Row>
        </RowsFrame>
        <Footnote>{longTermSentence(current, 'settings')}</Footnote>
        <Note>Saving applies to your district right away. The dashboard, student plans, and Program Evaluation all count newcomers and long-term MLs with these years.</Note>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pt: '16px', borderTop: `1px solid ${SETTINGS.divider}` }}>
          <Button variant="text" onClick={() => requestLeave(() => setForm(null))} disabled={busy}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <SaveFailed visible={saveFailed} />
          <Button variant="outlined" onClick={() => setForm({ ...YEARS_DEFAULTS })} disabled={busy}>
            Reset to defaults
          </Button>
          <Button variant="contained" onClick={onSave} disabled={busy}>
            Save years
          </Button>
        </Box>
      </SettingsCard>
    );
  }

  const atDefaults = !yearsDirty(saved, YEARS_DEFAULTS);
  const newcomerMark = saved.newcomer !== YEARS_DEFAULTS.newcomer ? `Changed from the default of ${YEARS_DEFAULTS.newcomer}` : undefined;
  const longTermMark = saved.longTerm !== YEARS_DEFAULTS.longTerm ? `Changed from the default of ${YEARS_DEFAULTS.longTerm}` : undefined;
  const startEditing = () => {
    setCacheMissed(false);
    setChangedSinceLoad(false);
    setForm({ ...saved });
  };
  const editButton = (
    <Button variant="text" onClick={startEditing} startIcon={<EditOutlinedIcon />}>
      Edit years
    </Button>
  );

  return (
    <SettingsCard labelledBy="years-title">
      {confirmation ? (
        <CardHeader aside={editButton}>
          <CardHeading id="years-title">Your district’s newcomer and long-term years</CardHeading>
          <CardSubline>{COUNTED_FROM}</CardSubline>
          <ConfirmedLine>{confirmedLine(confirmation)}</ConfirmedLine>
        </CardHeader>
      ) : (
        <CardHeader aside={<NotConfirmedPill />}>
          <CardHeading id="years-title">Newcomer and long-term years</CardHeading>
          <CardSubline>{atDefaults ? `Flashlight defaults. ${COUNTED_FROM}` : COUNTED_FROM}</CardSubline>
        </CardHeader>
      )}
      <RowsFrame>
        <ValueRow label="Newcomer" text={NEWCOMER_TEXT} value={yearsLabel(saved.newcomer)} mark={newcomerMark} />
        <ValueRow label="Long-term ML" text={LONG_TERM_TEXT} value={yearsLabel(saved.longTerm)} mark={longTermMark} />
      </RowsFrame>
      <Footnote>{longTermSentence(saved, confirmation || !atDefaults ? 'settings' : 'defaults')}</Footnote>
      {cacheMissed && (
        <Typography role="alert" sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.mark }}>
          {CACHE_MISSED_MESSAGE}
        </Typography>
      )}
      {changedSinceLoad && (
        <Typography role="alert" sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.mark }}>
          {YEARS_CHANGED_MESSAGE}
        </Typography>
      )}
      {!confirmation && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
          <SaveFailed visible={saveFailed} />
          <Button variant="outlined" onClick={startEditing} disabled={busy}>
            Edit years
          </Button>
          <Button variant="contained" onClick={onConfirm} disabled={busy}>
            These match our district
          </Button>
        </Box>
      )}
    </SettingsCard>
  );
}
